import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import api from '../services/api';
import Layout from '../components/Layout';

// Point the worker at the matching CDN build (avoids CRA webpack worker bundling issues)
pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

// ─── Constants ────────────────────────────────────────────────────────────────

const SKILL_SUGGESTIONS = [
  'Python', 'SQL', 'Power BI', 'Excel', 'Machine Learning',
  'Data Analysis', 'AI Engineering', 'FastAPI', 'React', 'Deep Learning',
  'TensorFlow', 'PyTorch', 'Tableau', 'Statistics', 'NLP',
  'Django', 'NumPy', 'Pandas', 'Scikit-learn', 'AWS',
];

const TARGET_ROLE_SUGGESTIONS = [
  'Data Analyst', 'AI Engineer', 'ML Engineer', 'Data Scientist',
  'Full Stack Developer', 'Business Analyst', 'Data Engineer',
  'Software Engineer', 'Backend Developer', 'Frontend Developer',
];

const WORK_MODES   = ['Remote', 'Hybrid', 'Onsite'];
const WM_TO_API   = { Remote: 'REMOTE', Hybrid: 'HYBRID', Onsite: 'ONSITE' };
const WM_FROM_API = { REMOTE: 'Remote', HYBRID: 'Hybrid', ONSITE: 'Onsite' };

const inputCls = 'w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors';

const EMPTY_FORM = {
  roleType:          'Fresher',
  jobTitle:          '',
  years_experience:  '',
  skills:            [],
  target_role:       '',
  target_companies:  [],
  preferred_location:'',
  work_mode:         '',
  degree:            '',
  college:           '',
  graduation_year:   '',
};

// ─── PDF text extraction (uses installed pdfjs-dist package) ─────────────────

const extractPdfText = async (file) => {
  const buffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: buffer });
  const pdf = await loadingTask.promise;
  const parts = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page    = await pdf.getPage(i);
    const content = await page.getTextContent();
    // Join items; add a space between words and a newline between lines
    parts.push(content.items.map((item) => item.str).join(' '));
  }
  const text = parts.join('\n').trim();
  if (!text) throw new Error('PDF appears to contain no readable text (scanned image?).');
  return text;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);
  const cls = type === 'success'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : 'bg-red-50 border-red-200 text-red-800';
  return (
    <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${cls}`}>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const TagInput = ({ tags, onChange, placeholder, suggestions = [] }) => {
  const [val, setVal]           = useState('');
  const [showSugg, setShowSugg] = useState(false);

  const add = (tag) => {
    const t = tag.trim().replace(/,+$/, '');
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setVal('');
  };

  const remove = (i) => onChange(tags.filter((_, idx) => idx !== i));

  const filtered = suggestions.filter(
    (s) => !tags.includes(s) && s.toLowerCase().includes(val.toLowerCase()),
  );

  return (
    <div>
      <div className="min-h-[42px] border border-gray-300 rounded-xl px-3 py-2 flex flex-wrap gap-2 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition-colors cursor-text">
        {tags.map((tag, i) => (
          <span key={i} className="flex items-center gap-1 bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 rounded-lg">
            {tag}
            <button type="button" onClick={() => remove(i)} className="hover:text-red-500 transition-colors">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
        <input
          value={val}
          onChange={(e) => { setVal(e.target.value); setShowSugg(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(val); }
            else if (e.key === 'Backspace' && !val && tags.length) remove(tags.length - 1);
          }}
          onFocus={() => setShowSugg(true)}
          onBlur={() => setTimeout(() => setShowSugg(false), 150)}
          placeholder={tags.length === 0 ? placeholder : 'Add more...'}
          className="flex-1 min-w-[100px] outline-none text-sm bg-transparent py-0.5"
        />
      </div>
      {showSugg && filtered.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {filtered.slice(0, 9).map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={() => add(s)}
              className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-600 rounded-lg transition-colors"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SuggestInput = ({ value, onChange, suggestions, placeholder }) => {
  const [focused, setFocused] = useState(false);
  const filtered = value
    ? suggestions.filter((s) => s.toLowerCase().includes(value.toLowerCase()) && s !== value)
    : suggestions;

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder={placeholder}
        className={inputCls}
      />
      {focused && filtered.length > 0 && (
        <div className="absolute z-10 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
          {filtered.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={() => { onChange(s); setFocused(false); }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const profileToForm = (p) => ({
  roleType:          (!p.current_role || p.current_role === 'Fresher') ? 'Fresher' : 'Working Professional',
  jobTitle:          (p.current_role && p.current_role !== 'Fresher') ? p.current_role : '',
  years_experience:  p.years_experience ?? '',
  skills:            p.skills || [],
  target_role:       p.target_role || '',
  target_companies:  p.target_companies || [],
  preferred_location:p.preferred_location || '',
  work_mode:         WM_FROM_API[p.work_mode] || '',
  degree:            p.degree || '',
  college:           p.college || '',
  graduation_year:   p.graduation_year ?? '',
});

const CareerAIProfile = () => {
  const navigate     = useNavigate();
  const fileInputRef = useRef(null);

  const [mode,        setMode]        = useState('loading'); // 'loading' | 'choose' | 'form'
  const [profile,     setProfile]     = useState(null);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [extracting,  setExtracting]  = useState(false);
  const [extractMsg,  setExtractMsg]  = useState('');
  const [saving,      setSaving]      = useState(false);
  const [toast,       setToast]       = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    api.get('/api/ai/profile')
      .then((res) => {
        setProfile(res.data);
        setForm(profileToForm(res.data));
        setMode('form');
      })
      .catch((err) => {
        if (err.response?.status === 404) setMode('choose');
        else { showToast('Failed to load profile', 'error'); setMode('choose'); }
      });
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      showToast('Please upload a PDF file', 'error');
      return;
    }

    setExtracting(true);
    setExtractMsg('Reading your PDF...');
    try {
      let rawText;
      try {
        rawText = await extractPdfText(file);
      } catch (pdfErr) {
        const msg = pdfErr.message?.includes('no readable text')
          ? 'This PDF appears to be a scanned image and cannot be read as text. Please fill in your details manually.'
          : 'Could not read the PDF file. It may be corrupted or password-protected. Please fill in manually.';
        showToast(msg, 'error');
        setMode('form');
        return;
      }

      setExtractMsg('AI is reading your resume...');
      let extracted;
      try {
        const res = await api.post('/api/ai/profile/extract-resume', { resume_text: rawText });
        extracted = res.data;
      } catch (apiErr) {
        const detail = apiErr.response?.data?.detail;
        showToast(
          detail || 'AI could not process your resume right now. Please try again or fill in manually.',
          'error',
        );
        setMode('form');
        return;
      }

      const wm = extracted.work_mode
        ? WM_FROM_API[extracted.work_mode.toUpperCase()] || ''
        : '';

      setForm({
        roleType:          extracted.current_role && extracted.current_role !== 'Fresher'
                             ? 'Working Professional' : 'Fresher',
        jobTitle:          extracted.current_role && extracted.current_role !== 'Fresher'
                             ? extracted.current_role : '',
        years_experience:  extracted.years_experience ?? '',
        skills:            extracted.skills || [],
        target_role:       extracted.target_role || '',
        target_companies:  [],
        preferred_location:extracted.preferred_location || '',
        work_mode:         wm,
        degree:            extracted.degree || '',
        college:           extracted.college || '',
        graduation_year:   extracted.graduation_year ?? '',
      });
      setMode('form');
      showToast('Resume parsed! Review your details and save.', 'success');
    } finally {
      setExtracting(false);
      setExtractMsg('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.target_role) {
      showToast('Please enter your target role', 'error');
      return;
    }
    setSaving(true);
    const payload = {
      current_role:       form.roleType === 'Fresher' ? 'Fresher' : (form.jobTitle || 'Working Professional'),
      years_experience:   form.years_experience !== '' ? Number(form.years_experience) : null,
      skills:             form.skills,
      target_role:        form.target_role || null,
      target_companies:   form.target_companies,
      preferred_location: form.preferred_location || null,
      work_mode:          WM_TO_API[form.work_mode] || null,
      degree:             form.degree || null,
      college:            form.college || null,
      graduation_year:    form.graduation_year !== '' ? Number(form.graduation_year) : null,
    };
    try {
      const method = profile ? 'put' : 'post';
      const res    = await api[method]('/api/ai/profile', payload);
      setProfile(res.data);
      showToast('Profile saved!', 'success');
      if (!profile) setTimeout(() => navigate('/student/careerai'), 1200);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  // updater helper: setForm field by key
  const setField = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const isComplete =
    Boolean(form.target_role) &&
    form.skills.length >= 2 &&
    (form.roleType === 'Fresher' || Boolean(form.jobTitle));

  // ── Render ────────────────────────────────────────────────────────────────

  if (mode === 'loading') {
    return (
      <Layout>
        <div className="max-w-2xl space-y-4 animate-pulse">
          <div className="skeleton h-8 w-48" />
          <div className="skeleton h-4 w-64" />
          <div className="skeleton h-64 rounded-2xl" />
          <div className="skeleton h-48 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* Extraction overlay */}
      {extracting && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-xl flex flex-col items-center gap-4 max-w-sm w-full mx-4">
            <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center">
              <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
            <p className="text-gray-700 font-semibold text-center">{extractMsg}</p>
            <p className="text-xs text-gray-400 text-center">This takes a few seconds</p>
          </div>
        </div>
      )}

      {/* Hidden file input — always mounted so ref is stable */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Reset Profile confirmation modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reset Profile?</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will clear the form and return you to the setup screen. Your saved profile
              will remain in the database until you save new details.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setForm(EMPTY_FORM);
                  setMode('choose');
                  setShowResetConfirm(false);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors"
              >
                Yes, Reset
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl space-y-6 animate-fade-in">
        {/* Back navigation (when editing an existing profile) */}
        {profile && (
          <button
            type="button"
            onClick={() => navigate('/student/careerai')}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to CareerAI
          </button>
        )}

        {/* Page header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Career Profile</h1>
            <p className="text-sm text-gray-500 mt-1">
              Your profile personalizes AI assistance, study guidance, and career recommendations.
            </p>
          </div>
          {profile && isComplete && (
            <span className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-200">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              Profile Complete
            </span>
          )}
        </div>

        {profile?.updated_at && (
          <p className="text-xs text-gray-400 -mt-2">
            Last updated:{' '}
            {new Date(profile.updated_at).toLocaleDateString('en-IN', {
              day: 'numeric', month: 'long', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </p>
        )}

        {/* ── Choose mode ── */}
        {mode === 'choose' && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-4">How would you like to set up your profile?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Option A: Upload Resume */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="group text-left p-6 bg-white border-2 border-gray-200 hover:border-primary rounded-2xl transition-all duration-200 hover:shadow-md"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Upload Your Resume</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Upload a PDF and our AI will read it and auto-fill your profile.
                </p>
                <span className="text-xs font-medium text-primary">Choose PDF File →</span>
              </button>

              {/* Option B: Fill Manually */}
              <button
                type="button"
                onClick={() => setMode('form')}
                className="group text-left p-6 bg-white border-2 border-gray-200 hover:border-secondary rounded-2xl transition-all duration-200 hover:shadow-md"
              >
                <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-secondary/20 transition-colors">
                  <svg className="w-6 h-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Fill Manually</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Enter your professional details and career goals step by step.
                </p>
                <span className="text-xs font-medium text-secondary">Start Filling →</span>
              </button>
            </div>
          </div>
        )}

        {/* ── Form ── */}
        {mode === 'form' && (
          <div className="space-y-5">

            {/* Section 1: Professional Background */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
              <h2 className="font-semibold text-gray-900">Professional Background</h2>

              <Field label="Current Role">
                <div className="flex gap-2">
                  {['Fresher', 'Working Professional'].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, roleType: opt, jobTitle: '' }))}
                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium border-2 transition-all ${
                        form.roleType === opt
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </Field>

              {form.roleType === 'Working Professional' && (
                <Field label="Current Job Title">
                  <input
                    type="text"
                    value={form.jobTitle}
                    onChange={(e) => setForm((f) => ({ ...f, jobTitle: e.target.value }))}
                    placeholder="e.g. Data Analyst at TCS"
                    className={inputCls}
                  />
                </Field>
              )}

              <Field label="Years of Experience">
                <input
                  type="number"
                  min={0}
                  max={20}
                  value={form.years_experience}
                  onChange={(e) => setForm((f) => ({ ...f, years_experience: e.target.value }))}
                  placeholder="0"
                  className={inputCls}
                />
              </Field>

              <Field label="Skills" hint="Press Enter or comma to add · Click a suggestion to include it">
                <TagInput
                  tags={form.skills}
                  onChange={setField('skills')}
                  placeholder="e.g. Python, SQL, Machine Learning..."
                  suggestions={SKILL_SUGGESTIONS}
                />
              </Field>
            </div>

            {/* Section 2: Career Goals */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
              <h2 className="font-semibold text-gray-900">Career Goals</h2>

              <Field label="Target Role *">
                <SuggestInput
                  value={form.target_role}
                  onChange={setField('target_role')}
                  suggestions={TARGET_ROLE_SUGGESTIONS}
                  placeholder="e.g. Data Scientist"
                />
              </Field>

              <Field label="Target Companies" hint="Optional — companies you'd love to work at">
                <TagInput
                  tags={form.target_companies}
                  onChange={setField('target_companies')}
                  placeholder="e.g. Google, Amazon, Infosys..."
                  suggestions={[]}
                />
              </Field>

              <Field label="Preferred Location">
                <input
                  type="text"
                  value={form.preferred_location}
                  onChange={(e) => setForm((f) => ({ ...f, preferred_location: e.target.value }))}
                  placeholder="e.g. Hyderabad, Bangalore, Remote"
                  className={inputCls}
                />
              </Field>

              <Field label="Work Mode">
                <div className="flex gap-2 flex-wrap">
                  {['', ...WORK_MODES].map((opt) => (
                    <button
                      key={opt || 'any'}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, work_mode: opt }))}
                      className={`py-1.5 px-4 rounded-xl text-sm font-medium border-2 transition-all ${
                        form.work_mode === opt
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {opt || 'Any'}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            {/* Section 3: Education */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
              <h2 className="font-semibold text-gray-900">Education</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Degree">
                  <input
                    type="text"
                    value={form.degree}
                    onChange={(e) => setForm((f) => ({ ...f, degree: e.target.value }))}
                    placeholder="e.g. B.Tech, MBA"
                    className={inputCls}
                  />
                </Field>
                <Field label="Graduation Year">
                  <input
                    type="number"
                    min={1990}
                    max={2030}
                    value={form.graduation_year}
                    onChange={(e) => setForm((f) => ({ ...f, graduation_year: e.target.value }))}
                    placeholder="e.g. 2024"
                    className={inputCls}
                  />
                </Field>
              </div>

              <Field label="College / University">
                <input
                  type="text"
                  value={form.college}
                  onChange={(e) => setForm((f) => ({ ...f, college: e.target.value }))}
                  placeholder="e.g. IIT Hyderabad"
                  className={inputCls}
                />
              </Field>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between">
              {profile ? (
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  className="text-sm text-red-500 hover:text-red-700 transition-colors"
                >
                  Reset Profile
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setMode('choose')}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  ← Back
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="ml-auto px-6 py-2.5 bg-primary text-white font-medium text-sm rounded-xl hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-50"
              >
                {saving ? 'Saving...' : profile ? 'Update Profile' : 'Save Profile'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CareerAIProfile;
